<template>
  <div>
    <div
      v-if="showSuccessPage"
      class="top-up-success"
    >
      <top-up-success
        :amount="amount"
        :transfer="transfer"
        :show-success-page.sync="showSuccessPage"
        :payment-method="paymentMethod"
      />
    </div>
    <div v-else>
      <div
        class="bg-white list-card p-2"
        :class="hasStepGuide ? 'radius-content' : ''"
      >
        <div>
          <h4 class="mb-2">
            {{ $t('payment.wiseTransfer.titleFollowInstruction') }}
          </h4>
          <div class="mt-2 d-flex">
            <div class="step-number">
              1
            </div>
            <p class="font-14 font-weight-600">
              Please visit the link below to transfer funds from your Wise account (formerly TransferWise) to our Wise account with the message “Account Name” and receive an invoice.
            </p>
          </div>
          <div class="wallet-content">
            <div class="content-wise-wallet">
              <label class="font-14">Payment link:</label>
              <p
                class="text-wise-wallet d-flex align-items-center text-link-color cursor-pointer"
                data-testid="wise-wallet-address"
              >
                <a
                  class="text-link-color underline"
                  :href="wisePaymentInfo[0].paymentLink"
                  target="_blank"
                >
                  {{ wisePaymentInfo[0].paymentLink }}
                </a>
                <feather-icon
                  icon="ExternalLinkIcon"
                  style="margin-left: 8px"
                />
              </p>
            </div>
            <b-row class="mt-3">
              <b-col md="4">
                <label
                  data-testid="label-total-amount-wallet-wise"
                  class="font-20"
                >
                  {{ $t('addFundPage.creditCardPayment.textTotalAmount') }}
                </label>
              </b-col>
              <b-col
                data-testid="total-amount-wallet-wise"
                md="8"
              >
                <strong
                  data-testid="amount-wallet-wise"
                  class="mr-1 total-amount font-20"
                >
                  {{ totalAmount }}
                </strong>
                <strong
                  data-testid="currency-wise"
                  class="font-20"
                >
                  USD
                </strong>
              </b-col>
            </b-row>
          </div>

          <div class="mt-2">
            <div class="mt-2 d-flex">
              <div class="step-number">
                2
              </div>
              <p class="font-14 font-weight-600">
                {{ $t('payment.wiseTransfer.secondContentInstructionWise') }}
              </p>
            </div>
            <div :class="hasStepGuide ? 'top-up-on-step' : 'top-up-form-container'">
              <div
                class="add-fund-form"
              >
                <validation-observer ref="formValidation">
                  <b-form class="transactions-form">
                    <b-form-group
                      :label="$t('payment.wiseTransfer.labelIDTransactions')"
                      class="position-relative form-label"
                    >
                      <validation-provider
                        #default="{ errors }"
                        id="transactions"
                        name="transactions"
                        rules="required"
                      >
                        <b-input-group
                          class="input-group-merge width-custom w-100"
                          :class="errors.length > 0 ? 'is-invalid' : null"
                        >
                          <b-form-input
                            v-model="transactionId"
                            class="input-height"
                            placeholder="EX: 123456789"
                            :state="errors.length > 0 ? false : null"
                          />
                        </b-input-group>
                        <small
                          v-if="errors[0]"
                          class="text-danger"
                        >
                          {{ errors[0] }}
                        </small>
                      </validation-provider>

                      <div class="mt-2">
                        <p
                          class="d-flex align-items-center upload"
                          @click="$refs.refImage.$el.click()"
                        >
                          <b-img
                            fluid
                            alt="img"
                            :src="
                              require('@/assets/images/pages/payment/ic-attachment-white.svg')
                            "
                          />
                          <span>{{ $t('payment.wiseTransfer.placeHolderUploadImg') }}
                            <span class="font-14">(PDF, JPG, PNG)</span></span>
                        </p>
                        <b-form-file
                          v-model="imageFile"
                          ref="refImage"
                          plain
                          accept=".jpg, .png, .pdf"
                          :hidden="true"
                          @input="inputImageRenderer"
                        />

                        <p
                          v-if="fileUrl && imageFile && imageFile.name"
                          class="form-label font-small-2 text-success"
                          style="margin-top: 5px"
                        >
                          <span>{{
                            `${$t('payment.wiseTransfer.textUploaded')} ${
                              imageFile.name || fileUrl.name
                            } ${$t('payment.wiseTransfer.textUploaded')}!`
                          }}</span>
                        </p>

                        <small
                          v-if="imageError"
                          class="text-danger"
                        >
                          {{ $t('payment.wiseTransfer.errorRequiredAttachment') }}
                        </small>
                      </div>

                      <slide-step
                        class="mt-2"
                        :step="2"
                      />
                      <div
                        class="
                          d-flex
                          justify-content-between
                          font-14 font-medium
                          mb-2
                        "
                      >
                        <span>{{ $t('payment.wiseTransfer.placeHolderUploadImg') }}</span>
                        <span>2/2</span>
                      </div>

                      <div
                        class="
                          d-flex
                          align-items-center
                          justify-content-end
                          mt-2
                        "
                      >
                        <b-button
                          id="trigger-back"
                          class="mr-1 border-0 btn-back"
                          variant="secondary"
                          pill
                          @click.prevent="handleClickPrev"
                        >
                          {{ $t('stepGuideControl.stepVerifyInfo.textBack') }}
                        </b-button>
                        <btn-loading
                          id="trigger-top-up-wise"
                          span-id="trigger-top-up-wise"
                          class="btn-height btn-text-color"
                          variant-convert="btn-submit"
                          pill
                          :loading="loading"
                          @click.prevent="submit"
                        >
                          {{ $t('guidePopUp.guideTopUpWise.textDone') }}
                        </btn-loading>
                      </div>
                    </b-form-group>
                  </b-form>
                </validation-observer>
              </div>
              <div class="line-center">
                <div class="horizontal-divide" />
              </div>

              <div class="summary-form">
                <summary-amount
                  :amount="amount"
                  :payment-method="paymentMethod"
                  :transfer="transfer"
                  :amount-tip="amountTip || 0"
                  :current-tip="currentTip || 0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  VBTooltip,
  BButton,
  BFormInput,
  BInputGroup,
  BFormGroup,
  BForm,
  BImg,
  BFormFile, BRow, BCol,
} from 'bootstrap-vue'
import BtnLoading from '@/layouts/components/BtnLoading.vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'

import paymentMixin from '@/mixins/paymentMixin'
import stepGuideMixin from '@/mixins/stepGuideMixin'
import generalConfigsMixin from '@/mixins/generalConfigsMixin'
import numberFormatMixin from '@/mixins/numberFormatMixin'
import gaTrackingMixin from '@/mixins/gaTrackingMixin'
import envMixin from '@/mixins/envMixin'
import { toastification } from '@core/mixins/toast'
import { createNamespacedHelpers } from 'vuex'

import { ref } from '@vue/composition-api'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import { PAYMENT_METHOD, TICKET_TYPE } from '@/constants'
import SummaryAmount from './Summary.vue'
import TopUpSuccess from './TopUpSuccess.vue'
import SlideStep from '../SlideStep.vue'

const { mapGetters: mapGettersAuth } = createNamespacedHelpers('auth')
const { mapGetters, mapActions } = createNamespacedHelpers('file')
const { mapActions: mapPaymentActions, mapGetters: mapPaymentGetters } = createNamespacedHelpers('payment')

export default {
  components: {
    BCol,
    BRow,
    BtnLoading,
    BButton,
    BFormInput,
    BInputGroup,
    BFormGroup,
    BImg,
    BFormFile,
    BForm,
    ValidationProvider,
    ValidationObserver,
    SummaryAmount,
    TopUpSuccess,
    SlideStep,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  mixins: [
    paymentMixin,
    generalConfigsMixin,
    numberFormatMixin,
    toastification,
    stepGuideMixin,
    gaTrackingMixin,
    envMixin,
  ],

  props: {
    paymentMethod: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },

    transfer: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },

    amount: {
      type: Number,
      required: true,
    },

    account: {
      type: Object,
      required: true,
    },

    // isToggle: {
    //   type: Boolean,
    //   required: true,
    // },

    transactionInfo: {
      type: String,
      required: true,
    },

    wisePaymentInfo: {
      type: Object,
      required: true,
    },

    billing: {
      type: [Array, File],
      required: false,
      default() {
        return []
      },
    },

    amountTip: {
      type: Number || null,
      default: null,
    },
    currentTip: {
      type: Number || null,
      default: null,
    },
  },

  setup() {
    const refImage = ref(null)

    const { inputImageRenderer } = useInputImageRenderer(refImage, () => {})

    return {
      refImage,
      inputImageRenderer,
    }
  },

  data() {
    return {
      transactionId: this.transactionInfo || '',
      imageFile: this.billing,
      imageError: false,
      showSuccessPage: false,
    }
  },

  computed: {
    ...mapGettersAuth(['user']),
    ...mapGetters(['status', 'fileUrl', 'loading', 'message']),
    ...mapPaymentGetters(['errorPayment']),

    isInTrial() {
      return this.user?.isInTrial
    },

    amountValidate() {
      return !!(this.amount >= this.minAmount && this.amount <= this.maxAmount)
    },

    minAmount() {
      return 200
    },

    maxAmount() {
      return 100000
    },

    serviceCharge() {
      // return parseFloat(this.truncDigits(this.percentFee * this.amount)).toLocaleString('en-US')
      return this.amountFormat(this.percentFee * this.amount).toLocaleString(
        'en-US',
      )
    },

    percentFee() {
      return this.user?.data?.plans?.transferFee?.transferWisePaymentLink || 0
    },

    percentFeeTxt() {
      return `${(this.percentFee * 100).toFixed(2)}%`
    },

    totalAmount() {
      const fee = this.amountFormat(this.percentFee * this.amount)
      const tax = this.amountFormat(this.taxRate * this.amount)
      // return parseFloat(this.truncDigits(this.amount + fee + tax)).toLocaleString('en-US')
      return (this.amount + fee + tax + this.amountTip).toLocaleString('en-US')
    },
  },

  watch: {
    imageFile(val) {
      if (val) {
        this.upload(val)
        this.imageError = false
      }
    },
  },

  methods: {
    ...mapActions(['uploadFile']),
    ...mapPaymentActions(['createRecharge', 'getListTicket']),

    async upload(file) {
      if (file) {
        await this.uploadFile(file)
        if (this.status) {
          this.toastSuccess('Upload file success!')
          this.$emit('set-url', this.fileUrl)
        } else {
          this.toastFailure(this.message)
        }
      }
    },

    handleClickPrev() {
      this.$emit('on-prev', {
        amount: this.amount,
        transactionId: this.transactionId,
        billing: this.imageFile,
        currentTip: this.currentTip,
        amountTip: this.amountTip,
      })
    },

    async fetchListTicket() {
      const params = {
        type: TICKET_TYPE.RECHARGE_USER_BALANCE,
        status: ['opening'],
      }
      await this.getListTicket({ params })
    },

    submit() {
      if (!this.fileUrl) {
        this.imageError = true
      }

      this.$refs.formValidation.validate().then(async success => {
        if (success && this.fileUrl) {
          const params = {
            type: TICKET_TYPE.RECHARGE_USER_BALANCE,
            details: {
              note: this.transactionId,
              amount: this.amount,
              paymentMethod: PAYMENT_METHOD.TRANSFERWISE_LINK,
              coinType: this.paymentMethod.value,
              billingAttachment: this.fileUrl,
              coinNetwork: this.paymentMethod.value,
              tipAMAmount: this.amountTip,
              // ...(this.isToggle
              //   && this.account && { adsAccountId: this.account?.value }),
            },
          }

          await this.createRecharge(params)
          if (this.isOnlyEcomdyPlatform) {
            this.gaTrackingUserAddFund(params.details.amount, PAYMENT_METHOD.TRANSFERWISE) // GA Tracking
          }
          if (this.errorPayment.message) {
            this.toastFailure(this.errorPayment.message)
          } else {
            this.showSuccessPage = true
            this.fetchListTicket()
            this.$emit('handleShowSuccess', this.showSuccessPage)
          }
        }
      })
    },

    handleCopy(val) {
      window.navigator.clipboard
        .writeText(val)
        .then(() => {
          this.toastSuccess('Copy success!')
        })
        .catch(e => {
          this.toastFailure(e)
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/_variables.scss';
.text-link-color {
  color: var(--text-live-chat-color);
}

  .step-number{
    min-width: 20px;
    height: 20px;
    background: $green;
    border-radius: 50px;
    line-height: 20px;
    color: white;
    font-size: 12px;
    text-align: center;
    margin: 2px 5px 0px 0px;
  }

.upload {
  cursor: pointer;
  background: #f5f5f7;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 32px 0px;
  justify-content: center;

  img {
    max-width: 15px;
    margin-right: 5px;
  }
}

.top-up-success {
  max-width: 410px;
  margin: 0 auto;
  margin-top: 42px;
}

.btn-back {
  color: rgb(22 33 62 / 76%) !important;
  background-color: rgba(0, 0, 0, 0.05) !important;
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 600;
}

.top-up-on-step {
  .line-center {
    display: none;
  }
}

.top-up-form-container {
  display: flex;

  .add-fund-form {
    width: 50%;
  }

  .line-center {
    margin: 0 1.5rem;
  }

  .summary-form {
    width: 50%;
  }

  @media (max-width: 1399px) {
    display: block;

    .add-fund-form {
      width: 100%;
    }

    .line-center {
      display: none;
    }

    .summary-form {
      width: 100%;
    }
  }
}

.total-amount{
  color: #00B9FF;
}

.wallet-content {
  margin: 32px 0px;
  border: 1px solid #eeeeee;
  background: #f5f5f7;
  border-radius: 16px;
  padding: 20px;
}

.radius-content {
  border-radius: 0px 0px var(--border-radius-base) var(--border-radius-base) !important;
  padding-top: 1rem;
}
</style>
